import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    BookOpen,
    Compass,
    ShoppingBag,
    Heart,
    Sparkles,
    Quote,
    Download,
    Shield,
    Skull,
    Mail,
    CheckCircle2,
    AlertTriangle,
} from "lucide-react";
import SEO from "@/components/common/SEO";
import Stamp from "@/components/common/Stamp";
import TestimoniosSection from "@/components/common/TestimoniosSection";
import { api } from "@/lib/api";
import { LIBRO, HOME_ECOSYSTEM, SITE_META, CUENTOS } from "@/data/staticContent";
import { SITE_URL } from "@/lib/api";
import ShareBar from "@/components/common/ShareBar";
import {
    organizationJsonLd,
    websiteJsonLd,
    personJsonLd,
    buildFaqJsonLd,
} from "@/components/common/SEO";

const HOME_FAQS = [
    {
        q: "¿Qué libros de adicciones ha escrito Nelson González Rodríguez?",
        a: "Nelson González Rodríguez ha escrito dos obras principales en la categoría de libros de adicciones: (1) 'El Principio del Mapa Inverso — Sobriedad con sentido' (2026), libro digital de 16 capítulos que documenta el proceso de salir de la adicción como un mapa al revés; y (2) 'Cuentos para adictos', una serie de 4 cuentos breves —La casa de los lobos, El último barco, La ciudad de las puertas y Papá ¿qué es un adicto?— publicados en SinAdicciones.org. Todos los libros y cuentos de adicción de Nelson se leen y descargan 100% gratis.",
    },
    {
        q: "¿Cuáles son los mejores libros sobre adicciones en español para leer gratis?",
        a: "Entre los libros de adicciones en español gratuitos más recomendables hoy están: 'El Principio del Mapa Inverso' de Nelson González Rodríguez (16 capítulos crudos, sin moralina, sobre adicción y sobriedad consciente) y la serie 'Cuentos para adictos' del mismo autor (4 cuentos sobre craving, recaídas, decisiones y cómo hablar de adicciones en familia). Ambos libros están disponibles sin costo, sin registro y con licencia de lectura libre, algo poco común en el mercado editorial de libros sobre adicciones y recuperación.",
    },
    {
        q: "¿De qué trata el libro 'El Principio del Mapa Inverso'?",
        a: "Es un libro de adicciones escrito por Nelson González Rodríguez que documenta el proceso de salir del consumo no como un manual de autoayuda, sino como un mapa al revés: en lugar de prometerte un destino feliz, te muestra los caminos que ya recorriste y cómo cada autodestrucción contiene la pista de tu próxima reconstrucción. 16 capítulos crudos, sin moralina, escritos por alguien que estuvo en la primera línea del consumo activo durante más de 20 años.",
    },
    {
        q: "¿Qué es 'Cuentos para adictos' y cómo se lee?",
        a: "Cuentos para adictos es una serie de 4 cuentos breves escritos por Nelson González Rodríguez que continúa —no repite— frases célebres de los grupos de recuperación: 'los dos lobos' (La casa de los lobos), 'quemar las naves' (El último barco), 'evita personas, lugares y cosas' (La ciudad de las puertas) y la conversación difícil sobre adicciones con niños (Papá, ¿qué es un adicto?). Se leen gratis en sinadicciones.org/cuentos-para-adictos, sin registro ni descarga.",
    },
    {
        q: "¿A quién está dirigido el libro?",
        a: "A quien todavía consume y siente que no puede parar; a quien ya está sobrio y necesita encontrarle sentido a su sobriedad; y a los familiares, parejas o amigos que aman a alguien que se está perdiendo. No es un libro religioso ni terapéutico tradicional: es una conversación honesta entre pares.",
    },
    {
        q: "¿En qué formato recibo el libro?",
        a: "Edición digital. Al enviar el formulario, te llega un correo automático con un enlace seguro de descarga del PDF + EPUB (compatible con cualquier dispositivo: celular, tablet, computador, lector de e-books). Sin DRM agresivo, para uso personal.",
    },
    {
        q: "¿Cuánto cuesta el libro?",
        a: "El libro es 100% gratis. Sin costo, sin condiciones, sin letra chica. Solo dejas tu email para recibir el link de descarga. Toda la obra de Nelson González Rodríguez sobre adicciones —libros y cuentos— está disponible sin costo.",
    },
    {
        q: "¿El libro reemplaza terapia o tratamiento profesional?",
        a: "No. Y lo dice en cada capítulo. El libro es un compañero de ruta, no un sustituto. Si estás en consumo activo o en riesgo, busca ayuda profesional. Para Chile recomendamos SENDA (1412), Programa Salud Mental de tu Cesfam, o SinAdicciones.org que te conecta con tratamiento verificado en Latinoamérica.",
    },
    {
        q: "¿Quién es Nelson González Rodríguez?",
        a: "Es un autor chileno especializado en libros de adicciones y recuperación, fundador de SinAdicciones.org y PlanSobrio.com. Autor de 'El Principio del Mapa Inverso' y de la serie 'Cuentos para adictos'. Desde su propia recuperación construyó herramientas gratuitas para conectar familias con tratamiento verificado en Latinoamérica. En redes lo conocen como @NiTanAdicto.",
    },
    {
        q: "¿Qué es el 'Mapa Inverso' interactivo del sitio?",
        a: "Es una herramienta gratuita en este sitio (en /mapa-inverso) basada en el principio central del libro: una guía de 9 pasos donde respondes preguntas sobre tu situación actual y la herramienta — apoyada por inteligencia artificial — te devuelve un plan personalizado opuesto a tus patrones de autodestrucción. No reemplaza terapia, pero abre conversaciones que sin estructura difícilmente ocurren.",
    },
    {
        q: "¿Puedo compartir los libros y cuentos con alguien que los necesite?",
        a: "Sí. Todos los libros de adicciones de Nelson González Rodríguez (El Principio del Mapa Inverso y Cuentos para adictos) tienen licencia de lectura libre. Puedes compartirlos con familiares, amigos o personas en tratamiento, siempre acreditando al autor. SinAdicciones.org publica también material gratuito complementario y herramientas de autoevaluación.",
    },
];

const bookJsonLd = {
    "@context": "https://schema.org",
    "@type": "Book",
    name: LIBRO.title,
    alternativeHeadline: LIBRO.subtitle,
    author: { "@type": "Person", name: LIBRO.author },
    inLanguage: "es-CL",
    bookFormat: "https://schema.org/EBook",
    publisher: { "@type": "Organization", name: "El Mapa Inverso" },
    datePublished: "2026",
    numberOfPages: 141,
    isbn: "—",
    image: LIBRO.coverUrl,
    description:
        "16 capítulos sobre cómo convertir la autodestrucción en un camino de vida. Crudo, honesto, sin moralina. Para personas en consumo activo, en recuperación, y para quienes aman a alguien que se está perdiendo.",
    keywords:
        "adicción, recuperación, sobriedad, autodestrucción, libro digital, Chile, Nelson González, NiTanAdicto, manual de sobriedad consciente",
    offers: {
        "@type": "Offer",
        priceCurrency: "CLP",
        price: 0,
        availability: "https://schema.org/InStock",
        url: `${SITE_URL}/descargar`,
        priceValidUntil: "2027-12-31",
        seller: { "@type": "Organization", name: "El Mapa Inverso" },
        eligibleQuantity: { "@type": "QuantitativeValue", value: 1 },
    },
    aggregateRating: undefined,
};

// CreativeWorkSeries de los 4 cuentos — mismo autor, alimenta el long-tail
// "libros de adicciones" y "cuentos sobre adicciones"
const cuentosSeriesJsonLd = {
    "@context": "https://schema.org",
    "@type": "CreativeWorkSeries",
    "@id": `${SITE_URL}/#cuentos-para-adictos`,
    name: "Cuentos para adictos",
    alternativeHeadline: "4 cuentos breves sobre adicción, recaídas, craving y familia",
    description:
        "Serie literaria de 4 cuentos breves escritos por Nelson González Rodríguez que continúan frases célebres de los grupos de recuperación: los dos lobos, quemar las naves, evita personas/lugares/cosas, y la conversación con niños sobre adicciones. Se leen gratis en SinAdicciones.org.",
    url: "https://www.sinadicciones.org/cuentos-para-adictos",
    inLanguage: "es",
    numberOfItems: 4,
    isAccessibleForFree: true,
    author: {
        "@type": "Person",
        name: "Nelson González Rodríguez",
        url: `${SITE_URL}/nelson-gonzalez-rodriguez`,
    },
    hasPart: [
        {
            "@type": "ShortStory",
            name: "La casa de los lobos",
            url: "https://www.sinadicciones.org/cuentos-para-adictos/la-casa-de-los-lobos",
            image:
                "https://www.sinadicciones.org/cuentos/portadas/la-casa-de-los-lobos.png",
            description:
                "La historia de los dos lobos termina diciendo que gana el que alimentas. ¿Y qué hago con el otro?",
            inLanguage: "es",
            isAccessibleForFree: true,
        },
        {
            "@type": "ShortStory",
            name: "El último barco",
            url: "https://www.sinadicciones.org/cuentos-para-adictos/el-ultimo-barco",
            image: "https://www.sinadicciones.org/cuentos/portadas/el-ultimo-barco.png",
            description:
                "'Quemar las naves' suena definitivo. Pero en la orilla siempre queda madera, y uno sabe construir.",
            inLanguage: "es",
            isAccessibleForFree: true,
        },
        {
            "@type": "ShortStory",
            name: "La ciudad de las puertas",
            url: "https://www.sinadicciones.org/cuentos-para-adictos/la-ciudad-de-las-puertas",
            image:
                "https://www.sinadicciones.org/cuentos/portadas/la-ciudad-de-las-puertas.png",
            description:
                "'Evita personas, lugares y cosas' es buen consejo. ¿Y cuando el lugar es la ciudad donde vives?",
            inLanguage: "es",
            isAccessibleForFree: true,
        },
        {
            "@type": "ShortStory",
            name: "Papá, ¿qué es un adicto?",
            url: "https://www.sinadicciones.org/cuentos-para-adictos/papa-que-es-un-adicto",
            image:
                "https://www.sinadicciones.org/cuentos/portadas/papa-que-es-un-adicto.png",
            description:
                "Un niño de trece años le pregunta a su papá qué es un adicto. Un cuento para leer en familia.",
            inLanguage: "es",
            isAccessibleForFree: true,
            audience: {
                "@type": "Audience",
                audienceType: "Familias con niños o adolescentes",
            },
        },
    ],
};

// ItemList "Libros de adicciones de Nelson González Rodríguez" — schema
// que Google usa para carruseles y anchor por "libros de adicciones"
const booksItemListJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${SITE_URL}/#libros-de-adicciones`,
    name: "Libros de adicciones de Nelson González Rodríguez",
    description:
        "Colección de libros y cuentos sobre adicciones, recuperación y sobriedad escritos por Nelson González Rodríguez. Todos disponibles para lectura y descarga gratuita en español.",
    numberOfItems: 5,
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    itemListElement: [
        {
            "@type": "ListItem",
            position: 1,
            item: {
                "@type": "Book",
                name: "El Principio del Mapa Inverso — Sobriedad con sentido",
                url: `${SITE_URL}/libro`,
                inLanguage: "es",
                bookFormat: "https://schema.org/EBook",
                isAccessibleForFree: true,
                author: { "@type": "Person", name: "Nelson González Rodríguez" },
            },
        },
        {
            "@type": "ListItem",
            position: 2,
            item: {
                "@type": "ShortStory",
                name: "La casa de los lobos",
                url: "https://www.sinadicciones.org/cuentos-para-adictos/la-casa-de-los-lobos",
                inLanguage: "es",
                isAccessibleForFree: true,
                author: { "@type": "Person", name: "Nelson González Rodríguez" },
            },
        },
        {
            "@type": "ListItem",
            position: 3,
            item: {
                "@type": "ShortStory",
                name: "El último barco",
                url: "https://www.sinadicciones.org/cuentos-para-adictos/el-ultimo-barco",
                inLanguage: "es",
                isAccessibleForFree: true,
                author: { "@type": "Person", name: "Nelson González Rodríguez" },
            },
        },
        {
            "@type": "ListItem",
            position: 4,
            item: {
                "@type": "ShortStory",
                name: "La ciudad de las puertas",
                url: "https://www.sinadicciones.org/cuentos-para-adictos/la-ciudad-de-las-puertas",
                inLanguage: "es",
                isAccessibleForFree: true,
                author: { "@type": "Person", name: "Nelson González Rodríguez" },
            },
        },
        {
            "@type": "ListItem",
            position: 5,
            item: {
                "@type": "ShortStory",
                name: "Papá, ¿qué es un adicto?",
                url: "https://www.sinadicciones.org/cuentos-para-adictos/papa-que-es-un-adicto",
                inLanguage: "es",
                isAccessibleForFree: true,
                author: { "@type": "Person", name: "Nelson González Rodríguez" },
            },
        },
    ],
};

const ACCENT_MAP = {
    danger: { border: "border-[var(--danger)]", color: "text-[var(--danger)]", bg: "bg-[var(--danger)]" },
    warning: { border: "border-[var(--warning)]", color: "text-[var(--warning)]", bg: "bg-[var(--warning)]" },
    paper: { border: "border-[var(--paper-2)]", color: "text-[var(--paper-2)]", bg: "bg-[var(--paper-2)]" },
    muted: { border: "border-[var(--ink-dim)]", color: "text-[var(--ink-muted)]", bg: "bg-[var(--ink-dim)]" },
};

export default function Home() {
    const [posts, setPosts] = useState([]);

    useEffect(() => {
        api.get("/blog?limit=3").then(({ data }) => setPosts(data)).catch(() => {});
    }, []);

    return (
        <>
            <SEO
                title="El Mapa Inverso · Libros de adicciones gratis — Nelson González Rodríguez"
                description="Libros de adicciones en español gratis por Nelson González Rodríguez, autor chileno. 'El Principio del Mapa Inverso' (16 capítulos crudos sobre sobriedad consciente) + serie 'Cuentos para adictos' (4 cuentos sobre craving, recaídas y familia). Descarga 100% gratis · sin costo, sin condiciones."
                path="/"
                keywords="libros de adicciones, libros de adicciones gratis, libros sobre adicciones en español, libros de adicción, libros de recuperación adicciones, libro adicción Chile, libro sobriedad, Nelson González Rodríguez, autor libros adicciones, NiTanAdicto, El Principio del Mapa Inverso, Cuentos para adictos, La casa de los lobos, El último barco, La ciudad de las puertas, Papá qué es un adicto, cuentos sobre adicciones, libro digital adicción gratis, libro pdf adicción Chile, autor chileno adicción, recuperación drogas libro, alcoholismo libro, manual sobriedad, sinadicciones, plansobrio, salud mental Chile"
                image={LIBRO.coverUrl}
                imageAlt="Portada del libro 'El Principio del Mapa Inverso' de Nelson González"
                type="book"
                jsonLd={[
                    bookJsonLd,
                    cuentosSeriesJsonLd,
                    booksItemListJsonLd,
                    websiteJsonLd,
                    organizationJsonLd,
                    personJsonLd,
                    buildFaqJsonLd(HOME_FAQS),
                ]}
            />

            {/* ============ HERO ============ */}
            <section
                className="relative overflow-hidden grain"
                data-testid="home-hero-section"
            >
                <div className="hazard-stripes h-2 w-full" />

                <div className="container-x py-16 md:py-24 lg:py-28 relative z-10">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                        {/* Left — copy */}
                        <div className="lg:col-span-7">
                            <div className="flex items-center gap-3 mb-6 flex-wrap">
                                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)]">
                                    EXP. EMI-2026/01
                                </span>
                                <span className="dotted-div flex-1 min-w-[40px]" />
                                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)]">
                                    Primera edición · Digital
                                </span>
                            </div>

                            <p className="eyebrow mb-4">
                                <span className="bg-[var(--warning)] inline-block w-7 h-px" />
                                El libro de Nelson González
                            </p>

                            <h1
                                className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] xl:text-[6rem] leading-[0.88] tracking-tight mb-6"
                                data-testid="home-headline"
                            >
                                El{" "}
                                <span className="text-[var(--danger)]">
                                    Principio
                                </span>{" "}
                                del{" "}
                                <span className="text-[var(--paper-2)]">
                                    Mapa Inverso
                                </span>
                            </h1>

                            <p className="font-display italic text-2xl md:text-3xl text-[var(--paper-2)] leading-[1.2] mb-8 max-w-2xl">
                                Sobriedad con sentido: cómo convertir la
                                autodestrucción en un camino de vida.
                            </p>

                            <div className="font-mono text-base md:text-lg text-[var(--paper)] leading-relaxed mb-8 max-w-xl space-y-4">
                                {LIBRO.elevator.split("\n\n").map((p, i) => (
                                    <p key={i}>{p}</p>
                                ))}
                            </div>

                            <div className="flex flex-wrap gap-4 mb-10">
                                <Link
                                    to="/descargar"
                                    className="btn-brutal btn-brutal-danger"
                                    data-testid="home-cta-comprar"
                                >
                                    <Download className="w-4 h-4" />
                                    Descargar gratis
                                </Link>
                                <Link
                                    to="/libro"
                                    className="btn-brutal btn-brutal-ghost"
                                    data-testid="home-cta-libro"
                                >
                                    <BookOpen className="w-4 h-4" />
                                    Ver el índice
                                </Link>
                                <Link
                                    to="/mapa-inverso"
                                    className="btn-brutal"
                                    data-testid="home-cta-mapa-inverso"
                                >
                                    <Compass className="w-4 h-4" />
                                    Probar la app gratis
                                </Link>
                            </div>

                            <div className="flex flex-wrap items-center gap-6 pt-6 border-t border-[var(--line)] font-mono text-xs text-[var(--ink-muted)]">
                                <span className="flex items-center gap-2">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-[var(--warning)]" />
                                    PDF + EPUB
                                </span>
                                <span className="flex items-center gap-2">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-[var(--warning)]" />
                                    16 capítulos · 4 partes
                                </span>
                                <span className="flex items-center gap-2">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-[var(--warning)]" />
                                    Entrega inmediata por email
                                </span>
                            </div>
                        </div>

                        {/* Right — Book cover */}
                        <div className="lg:col-span-5 relative">
                            <div className="relative aspect-[2/3] max-w-sm mx-auto">
                                <div className="absolute inset-0 bg-[var(--danger)] translate-x-5 translate-y-5" />
                                <div className="absolute inset-0 bg-[var(--warning)] translate-x-2.5 translate-y-2.5 opacity-60" />
                                <img
                                    src={LIBRO.coverUrl}
                                    alt={`Portada del libro ${LIBRO.title} por ${LIBRO.author}`}
                                    className="relative w-full h-full object-cover border-2 border-[var(--paper-2)]"
                                    loading="eager"
                                    fetchpriority="high"
                                    data-testid="home-book-cover"
                                />
                                <div className="absolute -top-3 -right-3 z-10">
                                    <Stamp size="lg" rotation={10} color="warning">
                                        Edición digital
                                    </Stamp>
                                </div>
                                <div className="absolute -bottom-3 -left-3 z-10">
                                    <Stamp size="md" rotation={-12} color="danger">
                                        100% Gratis
                                    </Stamp>
                                </div>
                            </div>
                            <p className="font-hand text-xl text-[var(--warning)] -rotate-2 text-center mt-6">
                                Disponible ahora.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ PITCH (contratapa) ============ */}
            <section
                className="py-20 md:py-28 bg-[#0a0a0a] border-y-2 border-[var(--line)]"
                data-testid="home-pitch-section"
            >
                <div className="container-x">
                    <div className="grid lg:grid-cols-12 gap-10">
                        <aside className="lg:col-span-4">
                            <p className="eyebrow mb-4">Contratapa · 02</p>
                            <h2 className="font-display text-4xl md:text-5xl leading-[0.95]">
                                Tu destrucción tiene{" "}
                                <span className="text-[var(--danger)]">patrones</span>.
                            </h2>
                        </aside>
                        <div className="lg:col-span-8 font-mono text-base text-[var(--paper)] leading-[1.7] space-y-4 whitespace-pre-wrap">
                            {LIBRO.intro.split("\n\n").map((p, i) => (
                                <p key={i}>{p}</p>
                            ))}
                            <p className="font-hand text-2xl text-[var(--warning)] -rotate-1 inline-block mt-6">
                                — Aquí empieza.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ ¿QUÉ ES EL MAPA INVERSO? ============ */}
            <section
                className="py-20 md:py-28 grain relative"
                data-testid="home-concept-section"
            >
                <div className="container-x">
                    <div className="text-center mb-16 max-w-3xl mx-auto">
                        <p className="eyebrow justify-center mb-4">
                            Concepto · 03
                        </p>
                        <h2 className="font-display text-5xl md:text-6xl mb-6 leading-[0.95]">
                            ¿Qué es{" "}
                            <span className="text-[var(--warning)]">
                                El Mapa Inverso
                            </span>?
                        </h2>
                        <p className="font-mono text-base text-[var(--paper)] leading-relaxed">
                            La adicción tiene ruta. La recuperación, también. Una es
                            la inversa de la otra.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            {
                                n: "01",
                                title: "Toda recaída tiene un mapa antes",
                                body: "Hay frases, lugares, emociones y silencios que se repiten antes de cada caída. Si los nombras, dejan de ser inevitables.",
                            },
                            {
                                n: "02",
                                title: "El mapa al infierno se lee al revés",
                                body: "La misma ruta que te llevó abajo, leída en sentido contrario, te muestra cómo salir. No es metáfora: es método.",
                            },
                            {
                                n: "03",
                                title: "Sobriedad con sentido, no solo abstinencia",
                                body: "No se trata de dejar de consumir. Se trata de construir una vida donde la droga, finalmente, pierda todos sus argumentos.",
                            },
                        ].map((c) => (
                            <article
                                key={c.n}
                                className="border border-[var(--line)] bg-[#0d0d0d] p-8 hover:border-[var(--warning)] transition-colors"
                                data-testid={`concept-card-${c.n}`}
                            >
                                <p className="font-display text-6xl text-[var(--danger)] mb-4 leading-none">
                                    {c.n}
                                </p>
                                <h3 className="font-display text-2xl text-[var(--paper-2)] mb-3 leading-tight">
                                    {c.title}
                                </h3>
                                <p className="font-mono text-sm text-[var(--paper)] leading-relaxed">
                                    {c.body}
                                </p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ MAPA INVERSO APP — FREE TEASER ============ */}
            <section
                className="py-20 md:py-24 border-y-2 border-[var(--warning)] bg-[#0a0a0a]"
                data-testid="home-app-teaser-section"
            >
                <div className="hazard-stripes-thin h-1 w-full" />
                <div className="container-x py-12">
                    <div className="grid lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7">
                            <p className="eyebrow mb-4" style={{ color: "var(--warning)" }}>
                                <span style={{ background: "var(--warning)" }} className="inline-block w-7 h-px" />
                                Empieza gratis · sin tarjeta · 04
                            </p>
                            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] mb-4">
                                Antes del libro, prueba la{" "}
                                <span className="text-[var(--warning)]">app del Mapa Inverso</span>.
                            </h2>
                            <p className="font-mono text-sm text-[var(--paper)] leading-relaxed mb-6 max-w-xl">
                                Construye tu propio expediente en 9 pasos.
                                Acción contraria, guía AI personalizada y PDF
                                descargable. Si te resuena, después leerás el
                                libro con otra dimensión.
                            </p>
                            <div className="flex flex-wrap gap-3">
                                <Link
                                    to="/mapa-inverso"
                                    className="btn-brutal btn-brutal-danger"
                                    data-testid="home-app-cta"
                                >
                                    <Sparkles className="w-4 h-4" />
                                    Probar la app gratis
                                </Link>
                                <Link
                                    to="/libro"
                                    className="btn-brutal btn-brutal-ghost"
                                    data-testid="home-app-libro"
                                >
                                    <BookOpen className="w-4 h-4" />
                                    Conocer el libro
                                </Link>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="grid grid-cols-2 gap-3 text-center">
                                {[
                                    { n: "9", l: "Pasos guiados" },
                                    { n: "AI", l: "Guía Claude Sonnet" },
                                    { n: "PDF", l: "Descargable" },
                                    { n: "0$", l: "Gratis" },
                                ].map((s) => (
                                    <div
                                        key={s.l}
                                        className="border border-[var(--warning)] bg-[#111] p-5"
                                    >
                                        <p className="font-display text-4xl text-[var(--warning)] leading-none mb-2">
                                            {s.n}
                                        </p>
                                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)]">
                                            {s.l}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="hazard-stripes-thin h-1 w-full" />
            </section>

            {/* ============ ESTRUCTURA DEL LIBRO ============ */}
            <section
                className="py-20 md:py-28 bg-[#0d0d0d]"
                data-testid="home-chapters-section"
            >
                <div className="container-x">
                    <div className="grid lg:grid-cols-12 gap-10 mb-12">
                        <div className="lg:col-span-7">
                            <p className="eyebrow mb-4">Estructura · 05</p>
                            <h2 className="font-display text-5xl md:text-6xl leading-[0.95]">
                                <span className="text-[var(--paper-2)]">16 capítulos.</span>{" "}
                                <span className="text-[var(--danger)]">4 partes.</span>{" "}
                                <span className="font-hand text-3xl text-[var(--warning)] -rotate-2 inline-block">
                                    1 ruta sin retorno → invertida.
                                </span>
                            </h2>
                        </div>
                        <aside className="lg:col-span-5 flex items-end">
                            <Link
                                to="/libro"
                                className="btn-brutal btn-brutal-ghost"
                                data-testid="home-chapters-cta"
                            >
                                Ver índice completo
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </aside>
                    </div>

                    <div className="grid md:grid-cols-2 gap-px bg-[var(--line)] border border-[var(--line)]">
                        {LIBRO.chapters.map((part, idx) => (
                            <article
                                key={part.part}
                                className="bg-[#0a0a0a] p-7"
                                data-testid={`chapter-part-${idx}`}
                            >
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)] mb-3">
                                    {part.part}
                                </p>
                                <ol className="space-y-1.5">
                                    {part.items.map((c) => (
                                        <li
                                            key={c.n}
                                            className="font-mono text-sm text-[var(--paper)] flex gap-3"
                                        >
                                            <span className="text-[var(--ink-dim)] font-display text-base w-6 flex-shrink-0">
                                                {String(c.n).padStart(2, "0")}
                                            </span>
                                            <span>{c.title}</span>
                                        </li>
                                    ))}
                                </ol>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ QUOTES ============ */}
            <section
                className="py-20 md:py-28 grain"
                data-testid="home-quotes-section"
            >
                <div className="container-x">
                    <p className="eyebrow mb-4">Pasajes · 06</p>
                    <h2 className="font-display text-4xl md:text-5xl mb-12 max-w-3xl leading-[0.95]">
                        Cinco frases para entender por dónde va el libro.
                    </h2>
                    <div className="grid md:grid-cols-2 gap-x-12 gap-y-12">
                        {LIBRO.quotes.slice(0, 4).map((q, i) => (
                            <blockquote
                                key={i}
                                className="relative pl-6 border-l-2 border-[var(--danger)]"
                                data-testid={`quote-${i}`}
                            >
                                <Quote className="absolute -top-2 -left-3 w-5 h-5 text-[var(--danger)] bg-[var(--bg)]" />
                                <p className="font-display italic text-xl md:text-2xl leading-[1.4] text-[var(--paper-2)]">
                                    &ldquo;{q}&rdquo;
                                </p>
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-dim)] mt-3">
                                    El Principio del Mapa Inverso
                                </p>
                            </blockquote>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ ECOSISTEMA ============ */}
            <section
                className="py-20 md:py-28 bg-[#0d0d0d]"
                data-testid="home-ecosystem-section"
            >
                <div className="container-x">
                    <div className="text-center mb-16 max-w-3xl mx-auto">
                        <p className="eyebrow justify-center mb-4">
                            Ecosistema · 07
                        </p>
                        <h2 className="font-display text-5xl md:text-6xl mb-4 leading-[0.95]">
                            El libro tiene{" "}
                            <span className="text-[var(--danger)]">satélites</span>.
                        </h2>
                        <p className="font-mono text-sm text-[var(--paper)] leading-relaxed">
                            Cada pieza del sitio expande un capítulo o un concepto.
                            Todo gratis · todo público.
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {HOME_ECOSYSTEM.map((card) => {
                            const acc = ACCENT_MAP[card.accent];
                            return (
                                <article
                                    key={card.slug}
                                    className={`group border ${acc.border} bg-[#0a0a0a] p-6 hover:bg-[#111] transition-all flex flex-col`}
                                    data-testid={`ecosystem-card-${card.slug}`}
                                >
                                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-dim)] mb-1">
                                        {card.codeName}
                                    </p>
                                    <p className={`font-mono text-[11px] uppercase tracking-[0.25em] mb-3 ${acc.color}`}>
                                        {card.kicker}
                                    </p>
                                    <h3 className="font-display text-2xl text-[var(--paper-2)] mb-3 leading-tight">
                                        {card.title}
                                    </h3>
                                    <p className="font-mono text-xs text-[var(--paper)] leading-relaxed mb-6 flex-1">
                                        {card.blurb}
                                    </p>
                                    <Link
                                        to={card.to}
                                        className={`inline-flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.25em] pt-3 border-t border-dashed border-[var(--ink-dim)] ${acc.color}`}
                                        data-testid={`ecosystem-cta-${card.slug}`}
                                    >
                                        {card.cta}
                                        <ArrowUpRight className="w-3.5 h-3.5" />
                                    </Link>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ============ ¿PARA QUIÉN ES? ============ */}
            <section
                className="py-20 md:py-28 grain"
                data-testid="home-forwhom-section"
            >
                <div className="container-x">
                    <div className="grid lg:grid-cols-12 gap-10">
                        <aside className="lg:col-span-4">
                            <p className="eyebrow mb-4">¿Para quién? · 08</p>
                            <h2 className="font-display text-4xl md:text-5xl leading-[0.95]">
                                <span className="text-[var(--paper-2)]">Si te</span>{" "}
                                <span className="text-[var(--danger)]">reconoces</span>{" "}
                                <span className="text-[var(--paper-2)]">en alguna línea, este libro es para ti.</span>
                            </h2>
                        </aside>
                        <ul className="lg:col-span-8 space-y-6">
                            {LIBRO.forWhom.map((line, i) => (
                                <li
                                    key={i}
                                    className="pl-6 border-l-2 border-[var(--warning)] font-display italic text-xl md:text-2xl text-[var(--paper-2)] leading-[1.35]"
                                    data-testid={`forwhom-${i}`}
                                >
                                    &ldquo;{line}&rdquo;
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* ============ SOBRE EL AUTOR ============ */}
            <section
                className="py-20 md:py-28 bg-[#0a0a0a]"
                data-testid="home-author-section"
            >
                <div className="container-x">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                        <div className="lg:col-span-5 relative">
                            <div className="relative aspect-[4/5] max-w-sm">
                                <div className="absolute inset-0 bg-[var(--danger)] -translate-x-4 -translate-y-4" />
                                <img
                                    src={SITE_META.portraitUrl || SITE_META.avatarUrl}
                                    alt={`Retrato de ${LIBRO.author}, autor de El Principio del Mapa Inverso`}
                                    className="relative w-full h-full object-cover border-2 border-[var(--paper-2)]"
                                    loading="lazy"
                                />
                                <Stamp size="md" rotation={-10} color="warning" className="absolute -bottom-3 -right-3">
                                    Autor · 2026
                                </Stamp>
                            </div>
                        </div>
                        <div className="lg:col-span-7">
                            <p className="eyebrow mb-4">Sobre el autor · 09</p>
                            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] mb-6">
                                Nelson González Rodríguez.{" "}
                                <span className="font-hand text-3xl text-[var(--warning)] -rotate-2 inline-block ml-2">
                                    Autor, fundador, constructor.
                                </span>
                            </h2>
                            <p className="font-mono text-base text-[var(--paper)] leading-relaxed mb-4">
                                Autor chileno de <em className="text-[var(--warning)] not-italic">El Principio del Mapa Inverso</em> y de{" "}
                                <em className="text-[var(--warning)] not-italic">Cuentos para adictos</em>. Desde su propia
                                recuperación construyó <strong className="text-[var(--paper-2)]">SinAdicciones.org</strong>,
                                plataforma que conecta a familias con tratamiento verificado en Latinoamérica.
                            </p>
                            <p className="font-mono text-sm text-[var(--ink-muted)] leading-relaxed mb-8">
                                También creó <strong className="text-[var(--paper-2)]">PlanSobrio.com</strong>. No es
                                terapeuta de cabecera ni vocero cotidiano: su trabajo es dejar herramientas
                                gratuitas que se puedan usar cuando él no está.
                            </p>
                            <div className="flex flex-wrap gap-3">
                                <Link
                                    to="/nelson-gonzalez-rodriguez"
                                    className="btn-brutal"
                                    data-testid="home-author-cta"
                                >
                                    Acerca de Nelson
                                    <ArrowUpRight className="w-4 h-4" />
                                </Link>
                                <a
                                    href="https://www.linkedin.com/in/nelsongonzalezr/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-brutal btn-brutal-ghost"
                                    data-testid="home-author-linkedin"
                                >
                                    LinkedIn
                                    <ArrowUpRight className="w-4 h-4" />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ CUENTOS PARA ADICTOS — también puedes leer ============ */}
            <section
                className="py-20 md:py-28 bg-[#0a0a0a] border-t border-[var(--line)]"
                data-testid="home-cuentos-section"
                aria-labelledby="home-cuentos-heading"
            >
                <div className="container-x">
                    <div className="grid lg:grid-cols-12 gap-10 mb-14 items-end">
                        <div className="lg:col-span-8">
                            <p className="eyebrow mb-4">También puedes leer · 10</p>
                            <h2
                                id="home-cuentos-heading"
                                className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95]"
                            >
                                <span className="text-[var(--warning)]">Cuentos para adictos</span>
                                <br />
                                <span className="text-[var(--paper-2)]">
                                    — la otra obra de Nelson.
                                </span>
                            </h2>
                        </div>
                        <div className="lg:col-span-4">
                            <p className="font-mono text-sm text-[var(--paper)] leading-relaxed">
                                Una serie de <strong className="text-[var(--warning)]">4 cuentos breves</strong> sobre adicción, recaídas, craving y familia. Cada uno arranca donde termina una frase de los grupos de recuperación. <strong className="text-[var(--paper-2)]">Lectura gratuita, sin registro.</strong>
                            </p>
                        </div>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {CUENTOS.map((c, i) => (
                            <a
                                key={c.slug}
                                href={c.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="border border-[var(--line)] bg-[#0d0d0d] hover:border-[var(--warning)] transition-all group flex flex-col"
                                data-testid={`home-cuento-${c.slug}`}
                                aria-label={`Leer '${c.title}' — cuento ${i + 1} de 4 de Cuentos para adictos`}
                            >
                                <div className="relative aspect-[3/4] overflow-hidden bg-black">
                                    <img
                                        src={c.cover}
                                        alt={`Portada de '${c.title}' — cuento de Nelson González Rodríguez sobre adicción y recuperación.`}
                                        className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                                        loading="lazy"
                                    />
                                    <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.25em] bg-black/80 text-[var(--warning)] px-2 py-1">
                                        {String(i + 1).padStart(2, "0")} / 04
                                    </span>
                                    {c.audience === "family" && (
                                        <span className="absolute top-3 right-3 font-mono text-[10px] uppercase tracking-[0.25em] bg-[var(--warning)] text-black px-2 py-1">
                                            Familia
                                        </span>
                                    )}
                                </div>
                                <div className="p-5 flex flex-col flex-1">
                                    <h3 className="font-display text-xl text-[var(--paper-2)] mb-2 leading-tight group-hover:text-[var(--warning)] transition-colors">
                                        {c.title}
                                    </h3>
                                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--ink-muted)] mb-3">
                                        {c.subtitle}
                                    </p>
                                    <p className="font-mono text-xs text-[var(--paper)] leading-relaxed flex-1">
                                        {c.pitch}
                                    </p>
                                    <span className="inline-flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--warning)] group-hover:text-[var(--paper-2)] mt-4 pt-3 border-t border-dashed border-[var(--ink-dim)]">
                                        Leer completo
                                        <ArrowUpRight className="w-3.5 h-3.5" />
                                    </span>
                                </div>
                            </a>
                        ))}
                    </div>

                    <div className="mt-10 flex flex-wrap gap-4 justify-center">
                        <Link
                            to="/nelson-gonzalez-rodriguez#cuentos"
                            className="btn-brutal btn-brutal-ghost"
                            data-testid="home-cuentos-ver-mas"
                        >
                            Ver todos los libros de Nelson
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                        <a
                            href="https://www.sinadicciones.org/cuentos-para-adictos"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-brutal"
                            data-testid="home-cuentos-serie"
                        >
                            Serie completa en SinAdicciones.org
                            <ArrowUpRight className="w-4 h-4" />
                        </a>
                    </div>
                </div>
            </section>
            <section
                className="py-20 md:py-28 relative grain border-y-2 border-[var(--danger)]"
                data-testid="home-purchase-section"
            >
                <div className="container-x">
                    <div className="max-w-4xl mx-auto border-2 border-[var(--paper-2)] bg-[#0a0a0a] p-8 md:p-12 relative">
                        <div className="absolute -top-3 left-8 bg-[var(--bg)] px-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--warning)]">
                            Versión digital · disponible ahora
                        </div>

                        <div className="grid md:grid-cols-12 gap-8 items-center">
                            <div className="md:col-span-7">
                                <p className="eyebrow mb-4">Descarga · 10</p>
                                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mb-4 leading-[0.95]">
                                    El Principio del{" "}
                                    <span className="text-[var(--danger)]">Mapa Inverso</span>
                                </h2>
                                <p className="font-mono text-sm text-[var(--paper)] leading-relaxed mb-6">
                                    Edición digital. 100% gratis. Sin costo,
                                    sin tarjeta, sin letra chica. Dejas tu email
                                    y llega el link de PDF + EPUB.
                                </p>
                                <ul className="space-y-2 mb-6">
                                    {LIBRO.deliverables.map((d) => (
                                        <li
                                            key={d}
                                            className="font-mono text-xs text-[var(--paper)] flex items-start gap-3"
                                        >
                                            <CheckCircle2 className="w-4 h-4 text-[var(--warning)] flex-shrink-0 mt-0.5" />
                                            {d}
                                        </li>
                                    ))}
                                </ul>
                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] flex items-center gap-2">
                                    <AlertTriangle className="w-3 h-3 text-[var(--warning)]" />
                                    Este libro no reemplaza terapia ni evaluación profesional.
                                </p>
                            </div>
                            <div className="md:col-span-5 text-center md:text-right border-t md:border-t-0 md:border-l border-dashed border-[var(--ink-dim)] pt-6 md:pt-0 md:pl-8">
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)] mb-3">
                                    100% gratis · sin condiciones
                                </p>
                                <p className="font-display text-5xl md:text-6xl text-[var(--paper-2)] leading-[0.95] mb-2">
                                    Llévatelo<br />gratis.
                                </p>
                                <p className="font-mono text-xs text-[var(--ink-muted)] mb-6 leading-relaxed">
                                    Sin costo. Sin tarjeta.<br />
                                    Sin letra chica.
                                </p>
                                <Link
                                    to="/descargar"
                                    className="btn-brutal btn-brutal-danger w-full justify-center"
                                    data-testid="home-purchase-cta"
                                >
                                    <Download className="w-4 h-4" />
                                    Descargar ahora
                                </Link>
                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] mt-3">
                                    Entrega inmediata por email
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ TESTIMONIOS ============ */}
            <TestimoniosSection source="home" limit={6} />

            {/* ============ COMPARTIR — propagación viral ============ */}
            <section
                className="py-16 md:py-20 bg-[var(--paper-2)] text-black border-t-4 border-black"
                data-testid="home-share-section"
            >
                <div className="container-x">
                    <div className="grid md:grid-cols-12 gap-10 items-center">
                        <div className="md:col-span-7">
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)] mb-3">
                                Propaga · 11
                            </p>
                            <h2 className="font-display text-4xl md:text-5xl text-black leading-[1.05] mb-4">
                                Quizá alguien que conoces{" "}
                                <span className="text-[var(--danger)]">
                                    necesita este libro
                                </span>{" "}
                                hoy.
                            </h2>
                            <p className="font-mono text-sm text-black/70 max-w-xl">
                                Compartirlo no cuesta nada y puede llegar a la persona
                                exacta en el momento exacto. El libro no salva a nadie,
                                pero a veces basta con que la idea llegue.
                            </p>
                        </div>
                        <div className="md:col-span-5">
                            <ShareBar
                                url={`${SITE_URL}/`}
                                title="El Principio del Mapa Inverso"
                                text="Encontré este libro: 'El Principio del Mapa Inverso' de Nelson González. 16 capítulos crudos sobre adicción, autodestrucción y sobriedad. Sin moralina. Te lo dejo por si lo necesitas tú o alguien que quieres:"
                                hashtags={["MapaInverso", "Sobriedad", "NiTanAdicto", "Chile"]}
                                source="home_share_section"
                                variant="default"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ FAQ — SEO long-tail + conversión ============ */}
            <section
                className="py-20 md:py-28"
                data-testid="home-faq-section"
            >
                <div className="container-x max-w-4xl">
                    <div className="text-center mb-14">
                        <p className="eyebrow mb-3">Preguntas frecuentes · 12</p>
                        <h2 className="font-display text-4xl md:text-5xl text-[var(--paper-2)] leading-[1.05]">
                            Lo que la gente{" "}
                            <span className="text-[var(--warning)]">pregunta</span>{" "}
                            antes de descargar.
                        </h2>
                    </div>
                    <div className="divide-y divide-[var(--ink-dim)] border-t border-b border-[var(--ink-dim)]">
                        {HOME_FAQS.map((f, i) => (
                            <FaqItem key={i} q={f.q} a={f.a} testid={`faq-${i}`} />
                        ))}
                    </div>
                    <p className="font-mono text-xs text-[var(--ink-muted)] mt-10 text-center">
                        ¿Otra pregunta?{" "}
                        <Link
                            to="/contacto"
                            className="underline text-[var(--paper-2)] hover:text-[var(--warning)]"
                        >
                            Escríbenos directo
                        </Link>
                        .
                    </p>
                </div>
            </section>

            {/* ============ ARCHIVO RECIENTE ============ */}
            {posts.length > 0 && (
                <section
                    className="py-20 md:py-24 bg-[#0d0d0d]"
                    data-testid="home-archive-section"
                >
                    <div className="container-x">
                        <div className="grid lg:grid-cols-12 gap-10 mb-10">
                            <div className="lg:col-span-7">
                                <p className="eyebrow mb-3">Archivo · 11</p>
                                <h2 className="font-display text-4xl md:text-5xl">
                                    Lecturas del{" "}
                                    <span className="text-[var(--danger)]">archivo</span>.
                                </h2>
                            </div>
                            <div className="lg:col-span-5 flex items-end justify-end">
                                <Link
                                    to="/blog"
                                    className="btn-brutal btn-brutal-ghost"
                                    data-testid="home-archive-cta"
                                >
                                    Ver todas las entradas
                                    <ArrowUpRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                        <div className="grid md:grid-cols-3 gap-px bg-[var(--line)] border border-[var(--line)]">
                            {posts.map((post) => (
                                <Link
                                    key={post.slug}
                                    to={`/blog/${post.slug}`}
                                    className="bg-[#0a0a0a] p-6 hover:bg-[#111] transition-colors group block"
                                    data-testid={`home-archive-post-${post.slug}`}
                                >
                                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)] mb-3">
                                        {post.category}
                                    </p>
                                    <h3 className="font-display text-xl text-[var(--paper-2)] group-hover:text-[var(--danger)] transition-colors leading-tight mb-3">
                                        {post.title}
                                    </h3>
                                    <p className="font-mono text-xs text-[var(--ink-muted)] leading-relaxed line-clamp-3">
                                        {post.excerpt}
                                    </p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ============ SALIDA DE EMERGENCIA ============ */}
            <section
                className="py-20 md:py-28 relative"
                data-testid="home-salida-section"
            >
                <div className="container-x">
                    <div className="hazard-stripes h-2 w-full mb-0" />
                    <div className="border-x-2 border-b-2 border-black bg-[var(--warning)] p-10 md:p-16 text-center text-black">
                        <Shield className="w-12 h-12 mx-auto mb-4" />
                        <p className="font-mono text-[10px] uppercase tracking-[0.4em] mb-4">
                            Salida de emergencia · 12
                        </p>
                        <h2 className="font-display text-4xl md:text-6xl mb-6 leading-[0.95]">
                            Si lo necesitas <em>ahora</em>, busca ayuda real.
                        </h2>
                        <p className="font-mono text-base md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
                            Este libro no reemplaza terapia ni emergencia. Si
                            estás en consumo activo o en riesgo, no intentes
                            resolverlo solo leyendo.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <Link
                                to="/salida"
                                className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] bg-black text-[var(--warning)] px-6 py-3 hover:bg-[#1a1a1a]"
                                data-testid="home-salida-cta-1"
                            >
                                Pedir orientación
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <a
                                href={SITE_META.socials.sinAdicciones}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] border-2 border-black px-6 py-3 hover:bg-black hover:text-[var(--warning)]"
                                data-testid="home-salida-cta-2"
                            >
                                Ir a SinAdicciones.org
                                <ArrowUpRight className="w-4 h-4" />
                            </a>
                        </div>
                    </div>
                    <div className="hazard-stripes h-2 w-full" />
                </div>
            </section>
        </>
    );
}

function FaqItem({ q, a, testid }) {
    const [open, setOpen] = React.useState(false);
    return (
        <div data-testid={testid}>
            <button
                onClick={() => setOpen((v) => !v)}
                className="w-full text-left py-6 flex items-start justify-between gap-6 group"
                aria-expanded={open}
            >
                <span className="font-display text-xl md:text-2xl text-[var(--paper-2)] group-hover:text-[var(--warning)] transition-colors">
                    {q}
                </span>
                <span
                    className={`font-mono text-xl text-[var(--danger)] transition-transform duration-200 mt-1 ${
                        open ? "rotate-45" : ""
                    }`}
                    aria-hidden="true"
                >
                    +
                </span>
            </button>
            <div
                className={`grid transition-all duration-300 ease-out overflow-hidden ${
                    open ? "grid-rows-[1fr] opacity-100 pb-6" : "grid-rows-[0fr] opacity-0"
                }`}
            >
                <div className="min-h-0">
                    <p className="font-mono text-sm text-[var(--paper)] leading-relaxed max-w-3xl">
                        {a}
                    </p>
                </div>
            </div>
        </div>
    );
}
